import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Loader2, CheckCircle, X, Heart, Share2 } from 'lucide-react';
import { resolveCaptureBySlug, collectCapture, recordPublicEvent, getCaptureLikes, likeCapture, unlikeCapture } from '../services/api';
import { EnergyVisualizer, generateFallbackEnergyMap } from '../components/EnergyVisualizer';
import { EVENT_NAMES, type PublicEventName } from '../config/eventCatalog';
import { getEventCorrelation, getOrCreatePersistentVisitorId } from '../utils/eventCorrelation';
import CaptureShareModal from '../components/capture-sharing/CaptureShareModal';
import { normalizeSharingSuggestions } from '../components/capture-sharing/normalizeSharingSuggestions';
import type { CaptureSharingSuggestion } from '../components/capture-sharing/types';
import { loadFansAudioPlayerResources } from '../components/audio/fansAudioPlayerLoader';

declare global {
  interface Window {
    FansAudioPlayer: any;
  }
}

interface TrackData {
  id: string;
  trackReference: string;
  title: string;
  coverArtUrl: string | null;
  streamUrl: string | null;
  isrc?: string | null;
  label?: string | null;
  streamLinks?: Array<{ service: string; serviceTrackId?: string | null; url: string; source?: string }>;
  hasAac: boolean;
  format: string;
  lyrics: any[] | null;
  enableLyrics: boolean;
  energyMap?: number[] | null;
}

interface CaptureData {
  id: number;
  tenantId: number;
  artistName?: string;
  title: string;
  description: string | null;
  slug: string;
  imageUrl: string | null;
  mediaType?: 'track' | 'video' | 'stream' | 'youtube';
  backgroundMode?: 'automatic' | 'solid' | 'image';
  backgroundColor?: string;
  backgroundImageUrl?: string | null;
  backgroundOverlayOpacity?: number;
  textColorMode?: 'automatic' | 'solid';
  textColor?: string;
  buttonColorMode?: 'automatic' | 'solid';
  buttonBgColor?: string;
  buttonFgColor?: string;
  buttonColor?: string;
  trackId: number | null;
  videoUrl?: string | null;
  youtubeUrl?: string | null;
  youtubeAspectRatio?: '16:9' | '9:16';
  membershipId: number | null;
  buttonText: string;
  buttonDelay: number;
  valueProp: string;
  collectButtonText: string;
  sharingPrompt?: string | null;
  audioStart: number;
  playbackType: 'preview' | 'full';
  playbackLength: number | null;
  aboutThisTrack?: string | null;
  sharingSuggestions?: CaptureSharingSuggestion[];
  track: TrackData | null;
}

interface CapturePageProps {
  tenantSlug: string | null;
  captureSlug: string;
}

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([A-Fa-f0-9]{6})$/.exec(hex);
  if (!m) return `rgba(0, 245, 255, ${alpha})`;
  const value = m[1];
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getReadableTextColor(hex: string): '#0D0D0D' | '#FFFFFF' {
  const m = /^#([A-Fa-f0-9]{6})$/.exec(hex);
  if (!m) return '#0D0D0D';
  const value = m[1];
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#0D0D0D' : '#FFFFFF';
}

function getCaptureEventCorrelation() {
  return getEventCorrelation(window.location.search, true);
}

function getSpotifyEmbedUrl(track: TrackData | null): string | null {
  if (!track?.streamLinks || !Array.isArray(track.streamLinks)) return null;
  const spotifyLink = track.streamLinks.find((row) => {
    const service = String(row.service || '').toLowerCase();
    const url = String(row.url || '').toLowerCase();
    return service.includes('spotify') || url.includes('open.spotify.com/track/');
  });
  if (!spotifyLink?.url) return null;

  const match = spotifyLink.url.match(/open\.spotify\.com\/track\/([A-Za-z0-9]+)/i);
  if (!match?.[1]) return null;
  return `https://open.spotify.com/embed/track/${match[1]}?utm_source=generator`;
}

function buildYoutubeEmbedUrl(baseUrl: string, options?: { autoplay?: boolean }): string {
  try {
    const url = new URL(baseUrl);
    url.searchParams.set('rel', '0');
    url.searchParams.set('modestbranding', '1');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('iv_load_policy', '3');
    if (options?.autoplay) {
      url.searchParams.set('autoplay', '1');
    }
    return url.toString();
  } catch {
    return baseUrl;
  }
}

function normalizePlatformService(service: string): string {
  return String(service || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

const STREAM_PLATFORM_LOGOS: Record<string, { label: string; logo: string }> = {
  spotify: { label: 'Spotify', logo: 'https://cdn.simpleicons.org/spotify/1DB954' },
  applemusic: { label: 'Apple Music', logo: 'https://cdn.simpleicons.org/applemusic/FA243C' },
  youtube: { label: 'YouTube', logo: 'https://cdn.simpleicons.org/youtube/FF0033' },
  deezer: { label: 'Deezer', logo: 'https://cdn.simpleicons.org/deezer/A238FF' },
  tidal: { label: 'TIDAL', logo: 'https://cdn.simpleicons.org/tidal/FFFFFF' },
};

function shuffleSuggestions<T>(input: T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function selectDisplaySuggestions(suggestions: CaptureSharingSuggestion[]): CaptureSharingSuggestion[] {
  if (suggestions.length <= 4) {
    return shuffleSuggestions(suggestions);
  }

  const positiveOrNeutral = suggestions.filter((s) => s.sentiment !== 'NEGATIVE');
  const negative = suggestions.filter((s) => s.sentiment === 'NEGATIVE');

  const picked: CaptureSharingSuggestion[] = [];
  const pickedIds = new Set<string>();

  const pickUniqueFrom = (source: CaptureSharingSuggestion[], count: number) => {
    const candidates = shuffleSuggestions(source).filter((s) => !pickedIds.has(s.id));
    for (const row of candidates.slice(0, count)) {
      picked.push(row);
      pickedIds.add(row.id);
    }
  };

  // Prefer 3 positive/neutral + 1 negative when available.
  pickUniqueFrom(positiveOrNeutral, 3);
  pickUniqueFrom(negative, 1);

  if (picked.length < 4) {
    pickUniqueFrom(suggestions, 4 - picked.length);
  }

  return shuffleSuggestions(picked).slice(0, 4);
}

type ModalState = 'hidden' | 'email' | 'loading' | 'error' | 'success';

const DEFAULT_SHARING_PROMPT = 'Got an opinion? Sharing helps this artist find their next fan.';

export default function CapturePage({ tenantSlug, captureSlug }: CapturePageProps) {
  const SHARE_PULSE_DELAY_MS = 1500;
  const SHARE_PULSE_DURATION_MS = 1200;
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<any>(null);
  const [capture, setCapture] = useState<CaptureData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resourcesReady, setResourcesReady] = useState(!!window.FansAudioPlayer);
  const [silkScreenVisible, setSilkScreenVisible] = useState(true);

  // CTA state
  const [ctaVisible, setCtaVisible] = useState(false);
  const ctaTimerRef = useRef<NodeJS.Timeout | null>(null);
  const playStartRef = useRef<boolean>(false);
  const onceEventsRef = useRef<Set<string>>(new Set());

  // Modal state
  const [modalState, setModalState] = useState<ModalState>('hidden');
  const [email, setEmail] = useState('');
  const [modalError, setModalError] = useState('');
  const [playerDimmed, setPlayerDimmed] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [likesLoaded, setLikesLoaded] = useState(false);
  const [likedByVisitor, setLikedByVisitor] = useState(false);
  const [likeInFlight, setLikeInFlight] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareInitialSuggestionId, setShareInitialSuggestionId] = useState<string | null>(null);
  const [sharePulseActive, setSharePulseActive] = useState(false);
  const sharePulseDelayTimerRef = useRef<number | null>(null);
  const sharePulseResetTimerRef = useRef<number | null>(null);
  const sharePulseConsumedRef = useRef(false);
  const shareModalOpenedThisVisitRef = useRef(false);
  const shareActionPerformedThisVisitRef = useRef(false);
  const videoPlaybackRef = useRef({
    isPlaying: false,
    startedAt: 0,
    watchedSeconds: 0,
  });

  const clearSharePulseTimers = () => {
    if (sharePulseDelayTimerRef.current !== null) {
      window.clearTimeout(sharePulseDelayTimerRef.current);
      sharePulseDelayTimerRef.current = null;
    }
    if (sharePulseResetTimerRef.current !== null) {
      window.clearTimeout(sharePulseResetTimerRef.current);
      sharePulseResetTimerRef.current = null;
    }
  };

  const prefersReducedMotion = () => {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  };

  const cancelPendingSharePulse = () => {
    if (sharePulseDelayTimerRef.current !== null) {
      window.clearTimeout(sharePulseDelayTimerRef.current);
      sharePulseDelayTimerRef.current = null;
    }
  };

  const scheduleSharePulse = () => {
    if (prefersReducedMotion()) return;
    if (sharePulseConsumedRef.current) return;
    if (sharePulseDelayTimerRef.current !== null) return;
    if (shareOpen) return;
    if (shareModalOpenedThisVisitRef.current) return;
    if (shareActionPerformedThisVisitRef.current) return;

    sharePulseDelayTimerRef.current = window.setTimeout(() => {
      sharePulseDelayTimerRef.current = null;
      if (sharePulseConsumedRef.current) return;
      if (shareOpen) return;
      if (shareModalOpenedThisVisitRef.current) return;
      if (shareActionPerformedThisVisitRef.current) return;

      sharePulseConsumedRef.current = true;
      setSharePulseActive(true);
      sharePulseResetTimerRef.current = window.setTimeout(() => {
        setSharePulseActive(false);
        sharePulseResetTimerRef.current = null;
      }, SHARE_PULSE_DURATION_MS);
    }, SHARE_PULSE_DELAY_MS);
  };

  // Eagerly load player resources
  useEffect(() => {
    loadFansAudioPlayerResources().then(() => setResourcesReady(true));
  }, []);

  // Fetch capture data
  useEffect(() => {
    onceEventsRef.current.clear();
    playStartRef.current = false;
    setCtaVisible(false);
    setSilkScreenVisible(true);
    setLikesCount(0);
    setLikedByVisitor(false);
    setLikesLoaded(false);
    setLikeInFlight(false);
    setShareOpen(false);
    setShareInitialSuggestionId(null);
    setSharePulseActive(false);
    clearSharePulseTimers();
    sharePulseConsumedRef.current = false;
    shareModalOpenedThisVisitRef.current = false;
    shareActionPerformedThisVisitRef.current = false;

    async function fetchCapture() {
      try {
        const res = await resolveCaptureBySlug(tenantSlug || null, captureSlug);
        if (!res.ok) {
          setError('Capture not found');
          return;
        }
        const json = await res.json() as { data: CaptureData };
        const normalizedCapture: CaptureData = {
          ...json.data,
          sharingSuggestions: normalizeSharingSuggestions(json.data.sharingSuggestions),
        };
        setCapture(normalizedCapture);
        if (normalizedCapture.mediaType === 'stream' || normalizedCapture.mediaType === 'youtube') {
          setSilkScreenVisible(false);
        }
        recordPublicEvent({
          event: EVENT_NAMES.CAPTURE_VIEW,
          captureId: normalizedCapture.id,
          ...getCaptureEventCorrelation(),
        }).catch(() => {});
      } catch {
        setError('Failed to load capture');
      } finally {
        setLoading(false);
      }
    }

    fetchCapture();
  }, [tenantSlug, captureSlug]);

  useEffect(() => {
    return () => {
      clearSharePulseTimers();
    };
  }, []);

  useEffect(() => {
    if (!capture?.id) return;

    let canceled = false;

    async function fetchLikes() {
      try {
        const visitorId = getOrCreatePersistentVisitorId();
        const res = await getCaptureLikes(capture.id, visitorId ? { visitorId } : {});
        if (!res.ok) return;

        const json = await res.json() as {
          data?: { count?: number; liked?: boolean };
        };

        if (canceled) return;
        setLikesCount(Number(json.data?.count || 0));
        setLikedByVisitor(Boolean(json.data?.liked));
        setLikesLoaded(true);
      } catch {
        if (!canceled) {
          setLikesLoaded(true);
        }
      }
    }

    fetchLikes();
    return () => {
      canceled = true;
    };
  }, [capture?.id]);

  const isVideoCapture = capture?.mediaType === 'video';
  const isStreamCapture = capture?.mediaType === 'stream';
  const isYoutubeCapture = capture?.mediaType === 'youtube';
  const youtubeAspectRatio = capture?.youtubeAspectRatio === '9:16' ? '9:16' : '16:9';

  const recordCaptureStat = (event: PublicEventName) => {
    if (!capture?.id) return;
    recordPublicEvent({
      event,
      captureId: capture.id,
      ...getCaptureEventCorrelation(),
    }).catch(() => {});
  };

  const recordCaptureStatOnce = (event: PublicEventName) => {
    if (onceEventsRef.current.has(event)) return;
    onceEventsRef.current.add(event);
    recordCaptureStat(event);
  };

  const recordPlaybackMilestones = (progress: number) => {
    if (progress >= 0.25) recordCaptureStatOnce(EVENT_NAMES.CAPTURE_PROGRESS_25);
    if (progress >= 0.5) recordCaptureStatOnce(EVENT_NAMES.CAPTURE_PROGRESS_50);
    if (progress >= 0.75) recordCaptureStatOnce(EVENT_NAMES.CAPTURE_PROGRESS_75);
    if (progress >= 1) recordCaptureStatOnce(EVENT_NAMES.CAPTURE_COMPLETE);
  };

  const revealCtaWithTracking = (delayMs: number) => {
    const reveal = () => {
      setCtaVisible(true);
      recordCaptureStatOnce(EVENT_NAMES.CAPTURE_CTA_EXPOSED);
    };

    if (delayMs <= 0) {
      reveal();
      return;
    }

    ctaTimerRef.current = setTimeout(reveal, delayMs);
  };

  // Initialize audio player once BOTH capture data AND resources are ready
  useEffect(() => {
    if (isVideoCapture || isStreamCapture || isYoutubeCapture) return;
    if (!capture || !capture.track || !capture.track.streamUrl || !resourcesReady) return;
    if (!containerRef.current || !window.FansAudioPlayer) return;
    // Don't mount player while silk screen is visible (no user gesture yet)
    if (silkScreenVisible) return;

    const trackData = {
      src: capture.track.streamUrl,
      title: capture.track.title,
      format: capture.track.format,
      lyrics: capture.track.enableLyrics ? (capture.track.lyrics || []) : [],
    };

    // Determine playback boundaries based on playbackType
    const isPreview = capture.playbackType === 'preview';
    const startAt = isPreview ? (capture.audioStart || 0) : 0;
    const endAt = isPreview && capture.playbackLength
      ? (capture.audioStart || 0) + capture.playbackLength
      : 0;

    playerRef.current = new window.FansAudioPlayer(containerRef.current, trackData, {
      autoStart: true,
      overlayPlayPause: true,
      repeat: false,
      compact: true,
      hideButtons: ['rewind', 'stop'],
      startAt,
      endAt,
    });
    playerRef.current.mount();

    // Start the CTA timer after play begins
    if (!playStartRef.current) {
      playStartRef.current = true;
      const delay = (capture.buttonDelay ?? 10) * 1000;
      revealCtaWithTracking(delay);
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (ctaTimerRef.current) {
        clearTimeout(ctaTimerRef.current);
      }
    };
  }, [capture, resourcesReady, silkScreenVisible, isVideoCapture, isStreamCapture, isYoutubeCapture]);

  // Start video playback after tap-to-play gate is dismissed.
  useEffect(() => {
    if (!capture || !isVideoCapture || silkScreenVisible || !videoRef.current) return;

    const video = videoRef.current;
    video.play().catch(() => {
      // Browser autoplay policy may still require explicit interaction in edge cases.
    });

    if (!playStartRef.current) {
      playStartRef.current = true;
      const delay = (capture.buttonDelay ?? 10) * 1000;
      revealCtaWithTracking(delay);
    }

    return () => {
      if (ctaTimerRef.current) {
        clearTimeout(ctaTimerRef.current);
      }
    };
  }, [capture, isVideoCapture, silkScreenVisible]);

  useEffect(() => {
    if (!capture || (!isStreamCapture && !isYoutubeCapture) || silkScreenVisible) return;
    if (!playStartRef.current) {
      playStartRef.current = true;
      const delay = (capture.buttonDelay ?? 10) * 1000;
      revealCtaWithTracking(delay);
    }
  }, [capture, isStreamCapture, isYoutubeCapture, silkScreenVisible]);

  const handleVideoPlay = () => {
    recordCaptureStatOnce(EVENT_NAMES.CAPTURE_PLAY);
    videoPlaybackRef.current.isPlaying = true;
    videoPlaybackRef.current.startedAt = Date.now();
  };

  const handleVideoPause = () => {
    if (videoPlaybackRef.current.isPlaying && videoPlaybackRef.current.startedAt > 0) {
      const elapsed = (Date.now() - videoPlaybackRef.current.startedAt) / 1000;
      videoPlaybackRef.current.watchedSeconds += Math.max(0, elapsed);
    }
    videoPlaybackRef.current.isPlaying = false;
    videoPlaybackRef.current.startedAt = 0;
  };

  const handleVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    if (duration <= 0) return;

    const progress = Math.max(0, Math.min(1, video.currentTime / duration));
    recordPlaybackMilestones(progress);
  };

  const handleVideoEnded = () => {
    recordCaptureStatOnce(EVENT_NAMES.CAPTURE_COMPLETE);
  };

  // Track audio milestones/progress against configured playback window.
  useEffect(() => {
    if (isVideoCapture || isStreamCapture || isYoutubeCapture) return;
    if (!capture || silkScreenVisible) return;

    let disposed = false;
    let rafId = 0;
    let audioEl: HTMLAudioElement | null = null;

    const attach = () => {
      if (disposed) return;
      const candidate = containerRef.current?.querySelector('audio') as HTMLAudioElement | null;
      if (!candidate) {
        rafId = requestAnimationFrame(attach);
        return;
      }

      audioEl = candidate;

      const isPreview = capture.playbackType === 'preview' && !!capture.playbackLength;
      const startAt = isPreview ? (capture.audioStart || 0) : 0;

      const onTimeUpdate = () => {
        const elapsed = Math.max(0, audioEl!.currentTime - startAt);
        const configuredDuration = isPreview
          ? Math.max(0, capture.playbackLength || 0)
          : Math.max(0, (Number.isFinite(audioEl!.duration) ? audioEl!.duration : 0) - startAt);

        if (configuredDuration <= 0) return;
        const progress = Math.max(0, Math.min(1, elapsed / configuredDuration));
        recordPlaybackMilestones(progress);
      };

      const onEnded = () => {
        recordCaptureStatOnce(EVENT_NAMES.CAPTURE_COMPLETE);
      };

      const onPlay = () => {
        recordCaptureStatOnce(EVENT_NAMES.CAPTURE_PLAY);
      };

      audioEl.addEventListener('timeupdate', onTimeUpdate);
      audioEl.addEventListener('ended', onEnded);
      audioEl.addEventListener('play', onPlay);

      const cleanup = () => {
        audioEl?.removeEventListener('timeupdate', onTimeUpdate);
        audioEl?.removeEventListener('ended', onEnded);
        audioEl?.removeEventListener('play', onPlay);
      };

      (audioEl as any).__captureCleanup = cleanup;
    };

    attach();

    return () => {
      disposed = true;
      if (rafId) cancelAnimationFrame(rafId);
      const cleanup = (audioEl as any)?.__captureCleanup;
      if (typeof cleanup === 'function') cleanup();
    };
  }, [capture, isVideoCapture, isStreamCapture, isYoutubeCapture, silkScreenVisible]);

  const handleTapToPlay = () => {
    setSilkScreenVisible(false);
  };

  const handleCtaClick = () => {
    recordCaptureStat(EVENT_NAMES.CAPTURE_CTA_CLICK);

    // Pause player
    if (!isVideoCapture && !isStreamCapture && !isYoutubeCapture && playerRef.current) {
      try {
        const audioEl = containerRef.current?.querySelector('audio');
        if (audioEl) audioEl.pause();
      } catch {}
    }
    if (isVideoCapture && videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {}
    }
    setPlayerDimmed(true);
    setModalState('email');
    setEmail('');
    setModalError('');
  };

  const handleLikeToggle = async () => {
    if (!capture?.id || likeInFlight) return;

    const currentlyLiked = likedByVisitor;
    const previousCount = likesCount;
    const nextLiked = !currentlyLiked;
    const nextCount = nextLiked
      ? previousCount + 1
      : Math.max(0, previousCount - 1);

    setLikeInFlight(true);
    setLikedByVisitor(nextLiked);
    setLikesCount(nextCount);

    try {
      const visitorId = getOrCreatePersistentVisitorId();
      const correlation = getCaptureEventCorrelation();
      const payload = {
        ...correlation,
        ...(visitorId ? { visitorId } : {}),
      };

      const res = currentlyLiked
        ? await unlikeCapture(capture.id, payload)
        : await likeCapture(capture.id, payload);

      if (!res.ok) {
        setLikedByVisitor(currentlyLiked);
        setLikesCount(previousCount);
        return;
      }

      const json = await res.json() as {
        data?: { liked?: boolean; count?: number };
      };
      const confirmedLiked = Boolean(json.data?.liked);
      setLikedByVisitor(confirmedLiked);
      setLikesCount(Number(json.data?.count ?? nextCount));
      if (!currentlyLiked && confirmedLiked) {
        scheduleSharePulse();
      }
    } catch {
      setLikedByVisitor(currentlyLiked);
      setLikesCount(previousCount);
    } finally {
      setLikeInFlight(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      setModalError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setModalError('Please enter a valid email address');
      return;
    }

    setModalState('loading');
    setModalError('');

    try {
      const res = await collectCapture(capture?.id!, {
        email: email.trim(),
        ...getCaptureEventCorrelation(),
      });

      if (!res.ok) {
        const errJson = await res.json() as { error: string };
        setModalError(errJson.error || 'Something went wrong. Please try again.');
        setModalState('error');
        return;
      }

      setModalState('success');
      scheduleSharePulse();
    } catch {
      setModalError('Network error. Please check your connection and try again.');
      setModalState('error');
    }
  };

  const handleBackToPlayer = () => {
    // Only hide the CTA if the user actually completed the email submission
    const wasSuccess = modalState === 'success';
    setModalState('hidden');
    setPlayerDimmed(false);
    if (wasSuccess) {
      setCtaVisible(false);
    }
    // Resume player
    if (!isVideoCapture && !isStreamCapture && !isYoutubeCapture) {
      try {
        const audioEl = containerRef.current?.querySelector('audio');
        if (audioEl) audioEl.play();
      } catch {}
    } else if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const shareSuggestions = capture?.sharingSuggestions || [];
  const displaySuggestions = useMemo(
    () => selectDisplaySuggestions(shareSuggestions),
    [capture?.id, shareSuggestions]
  );

  const openShareModal = () => {
    if (!capture) return;
    shareModalOpenedThisVisitRef.current = true;
    cancelPendingSharePulse();
    setShareInitialSuggestionId(null);
    setShareOpen(true);
  };

  const openShareModalWithReaction = (suggestionId: string) => {
    if (!capture) return;
    const suggestion = shareSuggestions.find((s) => s.id === suggestionId);
    if (!suggestion) {
      openShareModal();
      return;
    }

    shareModalOpenedThisVisitRef.current = true;
    cancelPendingSharePulse();
    setShareInitialSuggestionId(suggestion.id);
    setShareOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-electric-cyan/30 border-t-electric-cyan rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !capture) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex flex-col items-center justify-center px-4">
        <h1 className="text-xl font-bold text-white mb-2">Not Found</h1>
        <p className="text-sm text-text-secondary font-mono mb-6">{error || 'This page does not exist.'}</p>
      </div>
    );
  }

  const energyMap = capture.track?.energyMap || generateFallbackEnergyMap(capture.track?.trackReference || capture.slug);
  const spotifyEmbedUrl = getSpotifyEmbedUrl(capture.track);
  const resolvedArtistName = capture.artistName?.trim() || 'this artist';
  const resolvedValueProp = (capture.valueProp || 'Collect this track from {{artist}} and stay connected for future releases.')
    .replaceAll('{{artist}}', resolvedArtistName);

  // Foreground cover image only for audio captures.
  const displayImage = isVideoCapture ? null : (capture.imageUrl || capture.track?.coverArtUrl || null);
  const backgroundMode = capture.backgroundMode === 'solid' ? 'solid' : (capture.backgroundMode === 'image' ? 'image' : 'automatic');
  const backgroundColor = capture.backgroundColor || '#0D0D0D';
  const solidGlowColor = /^#([A-Fa-f0-9]{6})$/.test(backgroundColor) ? backgroundColor : '#0D0D0D';
  const backgroundImageUrl = capture.backgroundImageUrl || null;
  const backgroundOverlayOpacity = Number.isFinite(Number(capture.backgroundOverlayOpacity))
    ? Math.max(0, Math.min(90, Number(capture.backgroundOverlayOpacity)))
    : 35;
  const textColorMode = capture.textColorMode === 'solid' ? 'solid' : 'automatic';
  const textColor = /^#([A-Fa-f0-9]{6})$/.test(capture.textColor || '') ? (capture.textColor as string) : '#FFFFFF';
  const buttonColorMode = capture.buttonColorMode === 'solid' ? 'solid' : 'automatic';
  const buttonBgColor = /^#([A-Fa-f0-9]{6})$/.test(capture.buttonBgColor || '')
    ? (capture.buttonBgColor as string)
    : (/^#([A-Fa-f0-9]{6})$/.test(capture.buttonColor || '') ? (capture.buttonColor as string) : '#00F5FF');
  const buttonFgColor = /^#([A-Fa-f0-9]{6})$/.test(capture.buttonFgColor || '')
    ? (capture.buttonFgColor as string)
    : getReadableTextColor(buttonBgColor);
  const effectiveButtonBg = buttonColorMode === 'solid' ? buttonBgColor : '#00F5FF';
  const effectiveButtonFg = buttonColorMode === 'solid' ? buttonFgColor : '#0D0D0D';
  const effectiveButtonLoadingBg = buttonColorMode === 'solid' ? hexToRgba(buttonBgColor, 0.6) : 'rgba(0, 245, 255, 0.6)';
  const effectiveButtonShadow = buttonColorMode === 'solid'
    ? `0 0 10px ${hexToRgba(buttonBgColor, 0.6)}, 0 0 25px ${hexToRgba(buttonBgColor, 0.3)}, 0 0 45px ${hexToRgba(buttonBgColor, 0.15)}`
    : '0 0 10px rgba(0, 245, 255, 0.6), 0 0 25px rgba(0, 245, 255, 0.3), 0 0 45px rgba(0, 245, 255, 0.15)';
  const pulseColorMatch = /^#([A-Fa-f0-9]{6})$/.exec(effectiveButtonBg);
  const sharePulseRgb = pulseColorMatch
    ? `${parseInt(pulseColorMatch[1].slice(0, 2), 16)}, ${parseInt(pulseColorMatch[1].slice(2, 4), 16)}, ${parseInt(pulseColorMatch[1].slice(4, 6), 16)}`
    : '0, 245, 255';
  const showSharingSection = modalState === 'hidden';
  // Automatic background source for both media types.
  const autoBackgroundImage = capture.imageUrl || capture.track?.coverArtUrl || null;
  const effectiveImageBackground = backgroundImageUrl || autoBackgroundImage;

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center">
      {/* Layer 1: Background */}
      {backgroundMode === 'automatic' && autoBackgroundImage && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${autoBackgroundImage})`,
            filter: 'blur(60px)',
            transform: 'scale(1.2)',
          }}
        />
      )}
      {backgroundMode === 'image' && effectiveImageBackground && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${effectiveImageBackground})`,
            filter: 'none',
            transform: 'none',
          }}
        />
      )}
      {backgroundMode === 'solid' && (
        <>
          <div className="absolute inset-0" style={{ backgroundColor }} />

          {/* Solid mode glow layers (derived from selected solid color). */}
          <motion.div
            className="absolute inset-[-18%] pointer-events-none"
            style={{
              filter: 'blur(90px)',
              background: `
                radial-gradient(circle at 20% 20%, ${hexToRgba(solidGlowColor, 0.5)} 0%, transparent 55%),
                radial-gradient(circle at 80% 25%, ${hexToRgba(solidGlowColor, 0.35)} 0%, transparent 50%),
                radial-gradient(circle at 50% 80%, ${hexToRgba(solidGlowColor, 0.45)} 0%, transparent 60%)
              `,
            }}
            animate={{
              opacity: [0.35, 0.7, 0.35],
              scale: [1, 1.08, 1],
              x: ['0%', '2%', '0%'],
              y: ['0%', '-1%', '0%'],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />

          <motion.div
            className="absolute inset-[-22%] pointer-events-none"
            style={{
              filter: 'blur(120px)',
              background: `
                radial-gradient(circle at 30% 70%, ${hexToRgba(solidGlowColor, 0.3)} 0%, transparent 62%),
                radial-gradient(circle at 70% 40%, ${hexToRgba(solidGlowColor, 0.25)} 0%, transparent 58%)
              `,
            }}
            animate={{
              opacity: [0.25, 0.5, 0.25],
              scale: [1.02, 1.12, 1.02],
              x: ['0%', '-2%', '0%'],
              y: ['0%', '2%', '0%'],
            }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />
        </>
      )}

      {/* Dark overlay (automatic mode only) */}
      {backgroundMode === 'automatic' && <div className="absolute inset-0 bg-black/70" />}
      {backgroundMode === 'image' && (
        <div
          className="absolute inset-0"
          style={{ background: `rgba(0, 0, 0, ${backgroundOverlayOpacity / 100})` }}
        />
      )}

      {/* Layer 3: Foreground content */}
      <div className={`relative z-10 w-full max-w-4xl mx-auto px-4 pt-2 pb-8 sm:py-12 flex flex-col items-center transition-all duration-500 ${playerDimmed ? 'opacity-60 scale-[0.98]' : ''}`}>
        {/* Header: Capture Image + Title with Energy Visualizer background */}
        <div className="relative text-center mb-4 w-full max-w-4xl overflow-hidden rounded-lg px-[0.25em] py-[0.25em]">
          {/* Energy Visualizer behind image/title/description */}
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <EnergyVisualizer
              energyMap={energyMap}
              primaryColor="#00F5FF"
              backgroundColor="rgba(255,255,255,0.08)"
              barWidth={2}
              gap={1}
              progress={0}
              height="100%"
            />
          </div>

          {/* Content on top of energy visualizer */}
          <div className="relative z-10">
            {/* Capture Image (sharp, high-res square) */}
            {displayImage && (
              <div className="mx-auto mb-6 w-[200px] h-[200px] rounded-lg overflow-hidden shadow-[0_0_40px_rgba(0,245,255,0.15)]">
                <img
                  src={displayImage}
                  alt={capture.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Capture Title */}
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-2">
              <span style={textColorMode === 'solid' ? { color: textColor } : undefined}>{capture.title}</span>
            </h1>

            {/* Capture Description */}
            {capture.description && (
              <p className="text-sm font-mono tracking-wide uppercase text-text-secondary" style={textColorMode === 'solid' ? { color: textColor } : undefined}>
                {capture.description}
              </p>
            )}
          </div>
        </div>

        {/* Player Container */}
        <style>{`
          .fap-compact .fap-osc-grid { display: none !important; }
          .fap-waveform-ghost, .fap-waveform { opacity: 0 !important; transition: opacity 0.3s ease; }
          .fap-waveform-ghost.fap-waveform-active { opacity: 0.25 !important; }
          .fap-waveform.fap-waveform-active { opacity: 1 !important; }
          .fap-compact .fap-lyric-display { height: 0 !important; padding-top: 0 !important; margin-top: 0 !important; overflow: hidden; transition: height 0.2s ease; }
          .fap-compact .fap-lyric-display.fap-lyric-active { height: 52px !important; padding-top: 8px !important; margin-top: 8px !important; }
          .fap-compact .fap-player-wrap { max-height: 260px; overflow: hidden; }
          .fap-compact .fap-btn-shuffle,
          .fap-compact .fap-btn-repeat,
          .fap-compact .fap-btn-prev,
          .fap-compact .fap-btn-next,
          .fap-compact .fap-btn-playlist { display: none !important; }
        `}</style>
        {/* Player Box with CTA anchored to it */}
        <div className="relative w-full max-w-4xl">
          {isVideoCapture ? (
            <div className="w-full rounded-xl overflow-hidden border border-white/10 bg-black/50">
              <video
                ref={videoRef}
                src={capture.videoUrl || undefined}
                controls
                playsInline
                preload="metadata"
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onTimeUpdate={handleVideoTimeUpdate}
                onEnded={handleVideoEnded}
                className="w-full max-h-[70vh]"
              />
            </div>
          ) : isStreamCapture ? (
            <div className="w-full rounded-xl overflow-hidden bg-black/50 p-3 sm:p-4">
              {spotifyEmbedUrl ? (
                <iframe
                  title={capture.track?.title ? `${capture.track.title} on Spotify` : 'Spotify Player'}
                  src={spotifyEmbedUrl}
                  width="100%"
                  height="152"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  className="w-full rounded-lg"
                />
              ) : (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-300 text-sm px-4 py-3">
                  Spotify embed is unavailable for this track.
                </div>
              )}

              {Array.isArray(capture.track?.streamLinks) && capture.track!.streamLinks!.length > 0 && (
                <div className="mt-3 flex flex-wrap justify-center gap-4 py-2">
                  {capture.track!.streamLinks!.map((link, index) => {
                    const serviceKey = normalizePlatformService(link.service || '');
                    const platform = STREAM_PLATFORM_LOGOS[serviceKey];
                    return (
                      <a
                        key={`${link.service}-${index}`}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center"
                        title={`Open on ${platform?.label || link.service}`}
                      >
                        {platform ? (
                          <img
                            src={platform.logo}
                            alt={platform.label}
                            className="w-12 h-12 object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-[10px] font-mono uppercase tracking-wide text-white/90">
                            {link.service}
                          </span>
                        )}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          ) : isYoutubeCapture ? (
            <div className="w-full rounded-xl overflow-hidden bg-black/50 p-3 sm:p-4">
              {capture.youtubeUrl ? (
                <div className={`w-full mx-auto ${youtubeAspectRatio === '9:16' ? 'max-w-[420px]' : ''}`}>
                  <div
                    className="relative w-full overflow-hidden rounded-lg bg-black"
                    style={{ aspectRatio: youtubeAspectRatio === '9:16' ? '9 / 16' : '16 / 9' }}
                  >
                    <iframe
                      title={capture.title ? `${capture.title} on YouTube` : 'YouTube Player'}
                      src={buildYoutubeEmbedUrl(capture.youtubeUrl)}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-300 text-sm px-4 py-3">
                  YouTube embed is unavailable for this capture.
                </div>
              )}
            </div>
          ) : (
            <div ref={containerRef} className="w-full" />
          )}

          <div className="mt-4 flex items-center justify-between gap-4 px-2 sm:px-4">
            <div className="flex items-center gap-3 min-h-[44px]">
              <button
                type="button"
                onClick={handleLikeToggle}
                disabled={likeInFlight || !likesLoaded}
                aria-pressed={likedByVisitor}
                aria-label={likedByVisitor ? 'Unlike this capture' : 'Like this capture'}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition ${likedByVisitor ? 'border-rose-300/70 bg-rose-500/15 text-rose-100' : 'border-white/20 bg-black/35 text-white/90'} ${likeInFlight || !likesLoaded ? 'opacity-70 cursor-not-allowed' : 'hover:border-white/40 hover:bg-black/55'}`}
              >
                <Heart className={`h-4 w-4 ${likedByVisitor ? 'fill-current' : ''}`} />
                <span className="font-medium">{likedByVisitor ? 'Liked' : 'Like'}</span>
                {likesLoaded ? (
                  likesCount > 0 ? <span>{likesCount}</span> : null
                ) : (
                  <span>...</span>
                )}
              </button>
              <div className="h-7 w-px bg-white/15" aria-hidden="true" />
              <button
                type="button"
                onClick={openShareModal}
                className={`capture-share-button inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/35 px-3 py-2 text-xs uppercase tracking-[0.18em] text-white/80 transition hover:border-white/45 hover:bg-black/55 ${sharePulseActive ? 'capture-share-button--pulse' : ''}`}
                style={{ '--share-pulse-rgb': sharePulseRgb } as React.CSSProperties}
              >
                <Share2 className="h-3.5 w-3.5" />
                Share
              </button>
            </div>

            <AnimatePresence>
              {ctaVisible && modalState === 'hidden' && (
                <motion.button
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.9 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  onClick={handleCtaClick}
                  className="z-40 px-6 py-3 rounded-full font-bold text-sm text-[#0D0D0D] cursor-pointer"
                  style={{
                    background: effectiveButtonBg,
                    color: effectiveButtonFg,
                    boxShadow: effectiveButtonShadow,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {capture.buttonText || 'Collect this Track'}
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {showSharingSection ? (
            <div className="mt-3 px-2 sm:px-4 text-center">
              <p className="text-sm text-white/80" style={{ marginBottom: '1em' }}>
                {capture.sharingPrompt?.trim() || DEFAULT_SHARING_PROMPT}
              </p>
              <p className="text-[11px] uppercase tracking-wide text-white/60" style={{ marginBottom: '1em' }}>What's your take?</p>
              {displaySuggestions.length > 0 ? (
                <div className="mx-auto grid w-fit grid-cols-2 justify-items-center gap-[10px] sm:flex sm:w-full sm:max-w-none sm:flex-wrap sm:justify-center">
                  {displaySuggestions.map((suggestion) => (
                    <button
                      key={`take-${suggestion.id}`}
                      type="button"
                      onClick={() => openShareModalWithReaction(suggestion.id)}
                      className="inline-flex min-h-[42px] w-fit items-center justify-center rounded-[999px] border border-white/20 bg-black/25 px-[18px] py-[8px] text-center text-xs text-white/80 transition hover:border-white/45"
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

        </div>
      </div>

      {/* Glow Pulse keyframes — living energy source effect */}
      <style>{`
        .capture-share-button {
          position: relative;
          isolation: isolate;
        }

        .capture-share-button::after {
          content: '';
          position: absolute;
          inset: -6px;
          border-radius: 9999px;
          pointer-events: none;
          opacity: 0;
          transform: scale(0.96);
        }

        .capture-share-button--pulse::after {
          animation: shareButtonHaloPulse 1.2s ease-out 1;
        }

        @keyframes shareButtonHaloPulse {
          0% {
            opacity: 0;
            transform: scale(0.96);
            box-shadow: 0 0 0 0 rgba(var(--share-pulse-rgb, 0, 245, 255), 0.42);
          }
          20% {
            opacity: 0.95;
          }
          100% {
            opacity: 0;
            transform: scale(1.1);
            box-shadow: 0 0 0 14px rgba(var(--share-pulse-rgb, 0, 245, 255), 0);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .capture-share-button--pulse::after {
            animation: none !important;
          }
        }

        @keyframes ctaGlowPulse {
          0%, 100% {
            box-shadow: 0 0 10px rgba(0, 245, 255, 0.6), 0 0 25px rgba(0, 245, 255, 0.35), 0 0 45px rgba(0, 245, 255, 0.15);
          }
          50% {
            box-shadow: 0 0 20px rgba(0, 245, 255, 0.85), 0 0 45px rgba(0, 245, 255, 0.5), 0 0 70px rgba(0, 245, 255, 0.25);
          }
        }
      `}</style>

      <CaptureShareModal
        open={shareOpen}
        onClose={() => {
          setShareOpen(false);
          setShareInitialSuggestionId(null);
        }}
        captureId={capture.id}
        captureTitle={capture.title}
        artistName={capture.artistName || null}
        suggestions={shareSuggestions}
        initialSuggestionId={shareInitialSuggestionId}
        onShareCreated={() => {
          shareActionPerformedThisVisitRef.current = true;
        }}
        nativeShareButtonStyle={{
          background: effectiveButtonBg,
          color: effectiveButtonFg,
          boxShadow: effectiveButtonShadow,
        }}
      />

      <AnimatePresence>
        {modalState !== 'hidden' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 60 }}
              transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
              className="relative w-full max-w-md bg-[#1A1A1A]/95 border border-white/10 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl"
            >
              {/* Success State */}
              {modalState === 'success' && (
                <div className="p-8 text-center relative overflow-hidden">
                  {/* Confetti particles */}
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {Array.from({ length: 40 }).map((_, i) => {
                      const spreadX = (Math.random() - 0.5) * 300;
                      const spreadY = (Math.random() - 0.5) * 300;
                      return (
                        <motion.div
                          key={i}
                          initial={{
                            opacity: 1,
                            scale: 0,
                            x: 0,
                            y: 0,
                          }}
                          animate={{
                            opacity: [1, 1, 0],
                            x: spreadX,
                            y: spreadY,
                            scale: [0, 1.2, 0.6],
                            rotate: Math.random() * 720 - 360,
                          }}
                          transition={{
                            duration: 1.4 + Math.random() * 0.8,
                            delay: Math.random() * 0.3,
                            ease: 'easeOut',
                          }}
                          className="absolute w-2 h-2 rounded-sm"
                          style={{
                            left: '50%',
                            top: '50%',
                            backgroundColor: ['#00F5FF', '#4ade80', '#facc15', '#f472b6', '#a78bfa', '#fb923c'][i % 6],
                          }}
                        />
                      );
                    })}
                  </div>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                    className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center"
                  >
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </motion.div>
                  <h3 className="text-lg font-bold text-white mb-2">Access Granted</h3>
                  <p className="text-sm text-text-secondary mb-6">
                    Check your email for your My Collection link.
                  </p>
                  <button
                    onClick={handleBackToPlayer}
                    className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium text-sm transition-colors cursor-pointer"
                  >
                    Back to Player
                  </button>
                </div>
              )}

              {/* Email / Loading / Error States */}
              {modalState !== 'success' && (
                <div className="p-8">
                  {/* Value Prop */}
                  <p className="text-center text-white/90 text-base font-medium mb-6 leading-relaxed">
                    {resolvedValueProp}
                  </p>

                  {/* Email Input */}
                  <div className="space-y-4">
                    <div className="relative">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setModalError(''); }}
                        placeholder="your@email.com"
                        disabled={modalState === 'loading'}
                        className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan focus:ring-2 focus:ring-electric-cyan/40 focus:shadow-[0_0_15px_rgba(0,245,255,0.25)] transition-all duration-300 disabled:opacity-50"
                        onKeyDown={(e) => { if (e.key === 'Enter' && modalState === 'email') handleEmailSubmit(); }}
                        autoFocus
                      />
                      {/* Cyan glow on active — links input to CTA energy */}
                      <div className="absolute inset-0 rounded-xl pointer-events-none transition-all duration-300" style={{ boxShadow: email ? '0 0 20px rgba(0,245,255,0.15) inset, 0 0 8px rgba(0,245,255,0.1)' : 'none' }} />
                    </div>

                    {/* Error message */}
                    {modalError && (
                      <motion.p
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-xs text-red-400 px-1"
                      >
                        {modalError}
                      </motion.p>
                    )}

                    {/* Submit Button */}
                    <button
                      onClick={handleEmailSubmit}
                      disabled={modalState === 'loading'}
                      className="w-full py-3.5 rounded-xl font-bold text-sm text-[#0D0D0D] transition-all disabled:opacity-70 cursor-pointer"
                      style={{
                        background: modalState === 'loading' ? effectiveButtonLoadingBg : effectiveButtonBg,
                        color: effectiveButtonFg,
                        boxShadow: buttonColorMode === 'solid' ? `0 0 20px ${hexToRgba(buttonBgColor, 0.3)}` : '0 0 20px rgba(0, 245, 255, 0.3)',
                      }}
                    >
                      {modalState === 'loading' ? (
                        <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                      ) : (
                        capture.collectButtonText || 'Collect'
                      )}
                    </button>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={handleBackToPlayer}
                    className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <X className="w-4 h-4 text-text-muted" />
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Silk Screen Overlay - Tap to Play */}
      <AnimatePresence>
        {silkScreenVisible && !isStreamCapture && !isYoutubeCapture && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="absolute inset-0 z-50 flex flex-col items-center justify-center cursor-pointer"
            style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
            onClick={handleTapToPlay}
          >
            {/* Dark tint on silk screen */}
            <div className="absolute inset-0 bg-black/50" />

            {/* Play button + text */}
            <div className="relative z-10 flex flex-col items-center gap-6 text-center px-4">
              {/* Large play icon in circle */}
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-2 border-electric-cyan/60 bg-electric-cyan/10 flex items-center justify-center shadow-[0_0_60px_rgba(0,245,255,0.2)]"
              >
                <Play className="w-10 h-10 sm:w-14 sm:h-14 text-electric-cyan ml-1" fill="currentColor" />
              </motion.div>

              {/* Text */}
              <div>
                <p className="text-white/90 text-lg sm:text-xl font-medium">
                  Tap to experience
                </p>
                <p className="text-electric-cyan font-bold text-xl sm:text-2xl mt-1">
                  {isVideoCapture ? capture.title : (capture.track?.title || capture.title)}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
